import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  PenLine,
  Loader2,
  RefreshCw,
  Copy,
  ExternalLink,
  Check,
  X,
  Mail,
  Send,
} from "lucide-react";
import { api, type ComposerDraft } from "../lib/api";
import { TeachingBanner } from "../components/TeachingBanner";

/** Composer Agent surface (/composer).
 *
 *  Draft an email/message from an intent + tone, grounded in the
 *  thread's conversation summary. Regenerate with feedback. NEVER
 *  sends: the user copies to the target app or opens a mailto: link.
 *  Local Ollama (composer feature) by default. */

const CHANNELS = ["email", "sms", "whatsapp", "linkedin-message", "outlook", "teams", "other"];
const TONES = ["neutral", "warm", "professional", "brief", "apologetic", "celebratory"];

function mailtoFor(d: ComposerDraft): string {
  const subject = encodeURIComponent(d.subject ?? "");
  const body = encodeURIComponent(d.body_md ?? "");
  return `mailto:?subject=${subject}&body=${body}`;
}

export function Composer() {
  const [drafts, setDrafts] = useState<ComposerDraft[]>([]);
  const [selected, setSelected] = useState<ComposerDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [params] = useSearchParams();

  const [channel, setChannel] = useState("email");
  const [recipient, setRecipient] = useState("");
  const [intent, setIntent] = useState("");
  const [tone, setTone] = useState("neutral");
  const [prompt, setPrompt] = useState("");
  const [threadKey, setThreadKey] = useState("");
  const [feedback, setFeedback] = useState("");
  const [copied, setCopied] = useState(false);
  const [pushingTo, setPushingTo] = useState(false);
  const [pushOverride, setPushOverride] = useState("");

  const refresh = async () => {
    try {
      const r = await api.composerList("active");
      setDrafts(r.drafts ?? []);
    } catch {
      /* best effort */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Deep-link: /composer?thread_key=...&intent=...&channel=... opens
  // the form prefilled (used by the Forgotten "Draft a follow-up").
  useEffect(() => {
    const tk = params.get("thread_key");
    const it = params.get("intent");
    const ch = params.get("channel");
    if (tk) setThreadKey(tk);
    if (it) setIntent(it);
    if (ch && CHANNELS.includes(ch)) setChannel(ch);
  }, [params]);

  const createDraft = async () => {
    if (!intent.trim()) {
      setErr("Describe what the message should accomplish.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await api.composerDraft({
        target_channel: channel,
        intent: intent.trim(),
        tone,
        prompt: prompt.trim() || null,
        thread_key: threadKey.trim() || null,
        target_person_id: recipient.trim() || null,
      });
      setSelected(r.draft);
      setIntent("");
      setPrompt("");
      await refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    if (!selected) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.composerRegenerate(selected.id, {
        feedback: feedback.trim() || null,
        tone: selected.tone,
      });
      setSelected(r.draft);
      setFeedback("");
      await refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const act = async (kind: "finalize" | "dismiss") => {
    if (!selected) return;
    setBusy(true);
    try {
      const r =
        kind === "finalize"
          ? await api.composerFinalize(selected.id)
          : await api.composerDismiss(selected.id);
      setSelected(kind === "dismiss" ? null : r.draft);
      await refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const pushToGmail = async () => {
    if (!selected) return;
    setPushingTo(true);
    setErr(null);
    try {
      const r = await api.composerPushToGmail(selected.id, {
        to: pushOverride.trim() || null,
      });
      setSelected(r.draft);
      setPushOverride("");
      await refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setPushingTo(false);
    }
  };

  const copyBody = async () => {
    if (!selected) return;
    const text =
      (selected.subject ? `Subject: ${selected.subject}\n\n` : "") +
      selected.body_md;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
      <div className="space-y-4">
        <header className="space-y-1">
          <div className="text-xs uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500">
            Ambient
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            Composer
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Drafts only. Memso never sends. Copy into the app or open a
            pre-filled mail link.
          </p>
        </header>

        <TeachingBanner id="composer_page_overview" />

        <section className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-4 space-y-3">
          <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">
            New draft
          </h2>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
          >
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <input
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="Recipient (person id, optional)"
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
          />
          <input
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
            placeholder="Intent, e.g. follow up about the contract"
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
          />
          <select
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
          >
            {TONES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <input
            value={threadKey}
            onChange={(e) => setThreadKey(e.target.value)}
            placeholder="Thread key (optional, for replies)"
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
          />
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Extra guidance (optional)"
            rows={2}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
          />
          <button
            type="button"
            onClick={createDraft}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white text-sm font-semibold px-3 py-1.5"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <PenLine className="w-4 h-4" />
            )}
            Draft it
          </button>
          {err && (
            <div className="text-xs text-rose-600 dark:text-rose-300">{err}</div>
          )}
        </section>

        <section className="space-y-2">
          <h2 className="text-xs uppercase tracking-wide font-bold text-slate-400 dark:text-slate-500">
            Drafts
          </h2>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          ) : drafts.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              No drafts yet.
            </p>
          ) : (
            <ul className="space-y-1">
              {drafts.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(d)}
                    className={
                      "w-full text-left rounded-md px-2.5 py-1.5 text-sm border " +
                      (selected?.id === d.id
                        ? "border-sky-500 bg-sky-500/10"
                        : "border-slate-200 dark:border-slate-700 hover:border-sky-400")
                    }
                  >
                    <div className="font-semibold text-slate-800 dark:text-slate-100 truncate">
                      {d.subject || d.source_intent || "Untitled draft"}
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400">
                      {d.target_channel} · {d.tone} · {d.status} · v{d.draft_seq}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div>
        {selected ? (
          <section className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-5 space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Mail className="w-4 h-4 text-sky-500" />
              <span className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                {selected.target_channel} · {selected.tone} · v
                {selected.draft_seq} · {selected.status}
              </span>
            </div>
            {selected.subject && (
              <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
                {selected.subject}
              </div>
            )}
            <pre className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-200 font-sans">
              {selected.body_md}
            </pre>

            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                onClick={regenerate}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-60 text-xs font-semibold px-3 py-1.5"
              >
                {busy ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5" />
                )}
                Regenerate
              </button>
              <button
                type="button"
                onClick={copyBody}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-semibold px-3 py-1.5"
              >
                {copied ? (
                  <Check className="w-3.5 h-3.5 text-emerald-500" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
                Copy
              </button>
              {selected.target_channel === "email" && (
                <a
                  href={mailtoFor(selected)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-semibold px-3 py-1.5"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open in mail
                </a>
              )}
              {selected.target_channel === "email" && (
                <button
                  type="button"
                  onClick={pushToGmail}
                  disabled={pushingTo}
                  className="inline-flex items-center gap-1.5 rounded-md border border-sky-300 dark:border-sky-700 text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-950/40 disabled:opacity-60 text-xs font-semibold px-3 py-1.5"
                  title="Create a draft inside Gmail. You review and send from Gmail."
                >
                  {pushingTo ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                  {selected.gmail_draft_id ? "Update Gmail draft" : "Push to Gmail drafts"}
                </button>
              )}
              {selected.gmail_draft_url && (
                <a
                  href={selected.gmail_draft_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 text-xs font-semibold px-3 py-1.5"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open in Gmail
                </a>
              )}
              <button
                type="button"
                onClick={() => act("finalize")}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 disabled:opacity-60 text-xs font-semibold px-3 py-1.5"
              >
                <Check className="w-3.5 h-3.5" />
                Mark ready
              </button>
              <button
                type="button"
                onClick={() => act("dismiss")}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-60 text-xs font-semibold px-3 py-1.5"
              >
                <X className="w-3.5 h-3.5" />
                Dismiss
              </button>
            </div>

            <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
              <label className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                Feedback for the next revision
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="e.g. make it shorter, mention the deadline"
                rows={2}
                className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
              />
            </div>
            {selected.target_channel === "email" && (
              <div className="pt-1">
                <label className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  Recipient override (optional)
                </label>
                <input
                  value={pushOverride}
                  onChange={(e) => setPushOverride(e.target.value)}
                  placeholder="leave blank to use thread sender or linked person email"
                  className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                />
              </div>
            )}
          </section>
        ) : (
          <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-8 text-sm text-slate-500 dark:text-slate-400">
            Pick a draft or create a new one. Memso drafts; you send.
          </div>
        )}
      </div>
    </div>
  );
}
